"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, SlidersHorizontal, Check, X } from "lucide-react";

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp?: string;
}

const STORAGE_KEY = "beyond_ambition_cookie_consent";

// Helper to update Google Analytics / gtag consent state
function updateGtagConsent(prefs: CookiePreferences) {
  if (typeof window !== "undefined" && typeof (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag === "function") {
    (window as unknown as { gtag: (...args: unknown[]) => void }).gtag("consent", "update", {
      analytics_storage: prefs.analytics ? "granted" : "denied",
      ad_storage: prefs.marketing ? "granted" : "denied",
      ad_user_data: prefs.marketing ? "granted" : "denied",
      ad_personalization: prefs.marketing ? "granted" : "denied",
    });
  }
}

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: true,
    marketing: true,
  });

  useEffect(() => {
    // Check if user has already saved preferences
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: CookiePreferences = JSON.parse(stored);
        updateGtagConsent(parsed);
      } else {
        // User-interaction-triggered entrance so it never blocks LCP or first render
        const showBanner = () => {
          setIsVisible(true);
          window.removeEventListener("scroll", showBanner);
          window.removeEventListener("pointerdown", showBanner);
          window.removeEventListener("touchstart", showBanner);
          window.removeEventListener("keydown", showBanner);
        };

        window.addEventListener("scroll", showBanner, { passive: true, once: true });
        window.addEventListener("pointerdown", showBanner, { passive: true, once: true });
        window.addEventListener("touchstart", showBanner, { passive: true, once: true });
        window.addEventListener("keydown", showBanner, { passive: true, once: true });

        // Calming fallback after 6s for idle user
        const timer = setTimeout(showBanner, 6000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener("scroll", showBanner);
          window.removeEventListener("pointerdown", showBanner);
          window.removeEventListener("touchstart", showBanner);
          window.removeEventListener("keydown", showBanner);
        };
      }
    } catch {
      // Fallback for private browsing storage restrictions
      const timer = setTimeout(() => setIsVisible(true), 6000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Allow reopening consent modal dynamically (e.g. from Footer "Cookie Preferences" button)
  useEffect(() => {
    const handleOpenSettings = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setPreferences({
            essential: true,
            analytics: !!parsed.analytics,
            marketing: !!parsed.marketing,
          });
        }
      } catch {
        // ignore
      }
      setIsCustomizing(true);
      setIsVisible(true);
    };

    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => {
      window.removeEventListener("open-cookie-settings", handleOpenSettings);
    };
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    const consentPayload: CookiePreferences = {
      ...prefs,
      essential: true, // Always required
      timestamp: new Date().toISOString(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consentPayload));
    } catch {
      // ignore
    }

    updateGtagConsent(consentPayload);

    // Notify any listening components
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("cookie-consent-updated", { detail: consentPayload })
      );
    }

    setIsVisible(false);
    setIsCustomizing(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectNonEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSaveCustom = () => {
    saveConsent(preferences);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Cookie Consent Banner"
          aria-describedby="cookie-consent-desc"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
        >
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            onClick={() => setIsVisible(false)}
          />

          {/* Centered Modal Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-[500px] max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl bg-white/98 dark:bg-[#201A17]/98 backdrop-blur-2xl border border-outline-variant/30 dark:border-white/15 shadow-[0_25px_60px_rgba(0,0,0,0.5)] text-on-surface p-6 sm:p-7"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0 shadow-inner">
                  <Cookie className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold text-lg sm:text-xl text-on-surface tracking-tight leading-snug">
                    {isCustomizing ? "Cookie Preferences" : "We Value Your Privacy"}
                  </h2>
                  <p className="text-[11px] sm:text-xs text-on-surface-variant font-medium">
                    Industry-Standard Compliance
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsVisible(false)}
                className="text-on-surface-variant hover:text-on-surface p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer"
                aria-label="Dismiss cookie notice"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Main content or custom options */}
            {!isCustomizing ? (
              <>
                <p
                  id="cookie-consent-desc"
                  className="text-xs sm:text-[13px] text-on-surface-variant leading-relaxed mb-4"
                >
                  We use cookies and telemetry tools (such as Google Analytics and Microsoft Clarity) to optimize platform performance, analyze traffic, and enhance your digital experience. Read our{" "}
                  <Link
                    href="/privacy-policy"
                    className="text-primary hover:underline font-semibold"
                  >
                    Privacy Policy
                  </Link>{" "}
                  for full details.
                </p>

                {/* Primary Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="w-full sm:flex-1 py-2.5 px-4 bg-primary hover:bg-primary-hover text-white text-xs sm:text-sm font-semibold rounded-xl shadow-sm transition-all duration-150 cursor-pointer text-center active:scale-[0.98]"
                  >
                    Allow All
                  </button>

                  <button
                    type="button"
                    onClick={handleRejectNonEssential}
                    className="w-full sm:flex-1 py-2.5 px-3.5 bg-surface-container-high/60 hover:bg-surface-container-high text-on-surface text-xs sm:text-sm font-medium rounded-xl border border-outline-variant/20 dark:border-white/10 transition-all duration-150 cursor-pointer text-center active:scale-[0.98]"
                  >
                    Necessary Only
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsCustomizing(true)}
                    className="w-full sm:w-auto py-2.5 px-3 text-xs sm:text-sm font-medium text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high/40 rounded-xl transition-all duration-150 flex items-center justify-center gap-1.5 cursor-pointer"
                    title="Customize cookie choices"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    <span>Customize</span>
                  </button>
                </div>
              </>
            ) : (
              /* Granular Settings Drawer */
              <div className="space-y-3.5 pt-1">
                <p className="text-xs text-on-surface-variant leading-relaxed">
                  Customize your cookie preferences below. Strictly necessary cookies cannot be disabled as they ensure platform integrity and basic navigation.
                </p>

                {/* Item 1: Necessary */}
                <div className="p-3 rounded-xl bg-surface-container-low/80 dark:bg-surface-container-high/40 border border-outline-variant/15 flex items-start justify-between gap-3">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-semibold text-on-surface">Strictly Necessary</span>
                    </div>
                    <p className="text-[11px] text-on-surface-variant leading-normal">
                      Security, theme memory, and core functionality.
                    </p>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md shrink-0">
                    Always Active
                  </span>
                </div>

                {/* Item 2: Analytics & Telemetry */}
                <div className="p-3 rounded-xl bg-surface-container-low/80 dark:bg-surface-container-high/40 border border-outline-variant/15 flex items-start justify-between gap-3">
                  <div className="space-y-0.5">
                    <span className="text-xs font-semibold text-on-surface">Analytics & Telemetry</span>
                    <p className="text-[11px] text-on-surface-variant leading-normal">
                      Helps us analyze site speed, traffic sources, and user journeys via Google Analytics & Clarity.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={preferences.analytics}
                    onClick={() =>
                      setPreferences((prev) => ({
                        ...prev,
                        analytics: !prev.analytics,
                      }))
                    }
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      preferences.analytics ? "bg-primary" : "bg-outline-variant/40 dark:bg-white/20"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        preferences.analytics ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Item 3: Marketing & Personalization */}
                <div className="p-3 rounded-xl bg-surface-container-low/80 dark:bg-surface-container-high/40 border border-outline-variant/15 flex items-start justify-between gap-3">
                  <div className="space-y-0.5">
                    <span className="text-xs font-semibold text-on-surface">Experience & Personalization</span>
                    <p className="text-[11px] text-on-surface-variant leading-normal">
                      Saves your preferences, project scoping details, and consultation state.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={preferences.marketing}
                    onClick={() =>
                      setPreferences((prev) => ({
                        ...prev,
                        marketing: !prev.marketing,
                      }))
                    }
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      preferences.marketing ? "bg-primary" : "bg-outline-variant/40 dark:bg-white/20"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        preferences.marketing ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Preference Actions */}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleSaveCustom}
                    className="flex-1 py-2 px-3 bg-primary hover:bg-primary-hover text-white text-xs font-semibold rounded-xl shadow-sm transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Save Choices</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="py-2 px-3 bg-surface-container-high/60 hover:bg-surface-container-high text-on-surface text-xs font-medium rounded-xl border border-outline-variant/20 dark:border-white/10 transition-all cursor-pointer"
                  >
                    Allow All
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsCustomizing(false)}
                    className="py-2 px-2.5 text-xs text-on-surface-variant hover:text-on-surface rounded-xl transition-all cursor-pointer"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
